import React from 'react';
import { TableColumn } from '@/components/school-management/GenericTable/GenericTable';
import DropdownMenu from '@/components/school-management/DropdownMenu/DropdownMenu';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import Tooltip, { TooltipPosition } from '@/components/Tooltip/Tooltip';
import commonUtils from '@/school-management/utils/common';
import config from '@/config';
import classNames from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import type { ClassesReportTableRow } from './normalizeGroupReportRows';
import { formatPersonName } from '@/utils/personName';

type DropdownItem = {
  label: string;
  action: string;
  danger?: boolean;
  onClick: (id: string) => void;
};

function teacherEntryId(teacher: { userId?: string; id?: string }): string | undefined {
  return teacher.userId || teacher.id;
}

/** When `currentTeacherUserId` is set (teacher dashboard), returns only co-teachers. Otherwise all teachers. */
export function filterCoTeachersForViewer(
  teachers: ClassesReportTableRow['teachers'] | undefined,
  currentTeacherUserId: string | undefined
): NonNullable<ClassesReportTableRow['teachers']> {
  const list = Array.isArray(teachers) ? teachers : [];
  if (!currentTeacherUserId) {
    return list;
  }
  return list.filter(t => teacherEntryId(t) !== currentTeacherUserId);
}

function coTeachersDisplayString(
  teachers: ClassesReportTableRow['teachers'] | undefined,
  currentTeacherUserId: string | undefined
): string {
  const co = filterCoTeachersForViewer(teachers, currentTeacherUserId);
  if (currentTeacherUserId && co.length === 0) {
    return '-';
  }
  return co
    .map(t => formatPersonName(t?.firstName, t?.lastName))
    .filter(Boolean)
    .join(', ');
}

export function createSharedClassesReportColumns(options: {
  t: (key: string) => string;
  copiedClassId: string | null;
  setCopiedClassId: (id: string | null) => void;
  onCopyClassCode: (message: string) => void;
  /** Logged-in teacher: column shows co-teachers only; "-" when none. */
  currentTeacherUserId?: string;
  /** Optional click action for class name (e.g. teacher dashboard navigate to class view). */
  onNameClick?: (classId: string) => void;
}): TableColumn<ClassesReportTableRow>[] {
  const { t, copiedClassId, setCopiedClassId, onCopyClassCode, currentTeacherUserId, onNameClick } =
    options;

  return [
    {
      key: 'name',
      label: t('genericTable.classes.name'),
      width: '2fr',
      sortable: true,
      render: (_value, row) => (
        <div className={classNames.classNameCell}>
          {onNameClick ? (
            <button
              type='button'
              className={classNames.classNameLink}
              onClick={() => onNameClick(row.id)}
            >
              {row.name}
            </button>
          ) : (
            <div className={classNames.className}>{row.name}</div>
          )}
          {row.classCode && (
            <div className={classNames.classCode}>
              <span>{row.classCode}</span>
              <Tooltip
                content={t('common.copied') || ''}
                show={copiedClassId === row.id}
                position={TooltipPosition.Top}
                onHide={() => setCopiedClassId(null)}
              >
                <button
                  type='button'
                  className={classNames.copyBtn}
                  onClick={e => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(row.classCode || '');
                    onCopyClassCode(t('schoolDetailsPage.classes.copyClassCode') || '');
                    setCopiedClassId(row.id);
                    setTimeout(() => setCopiedClassId(null), 2000);
                  }}
                >
                  <AppIcon icon='copy' size={12} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
      xlsxRender: (_value, row) => `${row.name}`,
    },
    {
      key: 'teacher',
      label: currentTeacherUserId
        ? t('genericTable.classes.coTeacher')
        : t('genericTable.classes.teacher'),
      width: '2fr',
      sortable: true,
      render: (value, row) => {
        const teachers = filterCoTeachersForViewer(row.teachers, currentTeacherUserId);

        if (currentTeacherUserId && teachers.length === 0) {
          return (
            <div className={classNames.teacherCell}>
              <span className={classNames.teacherName}>-</span>
            </div>
          );
        }

        return (
          <div className={classNames.teacherCell}>
            {Array.isArray(teachers) && teachers.length > 0 ? (
              <div className={classNames.teacherNamesContainer}>
                {teachers.map((teacher, index) => {
                  const firstName = (teacher?.firstName || '').trim();
                  const lastName = (teacher?.lastName || '').trim();
                  const fullName = [firstName, lastName].filter(Boolean).join('\u00A0');

                  return (
                    <React.Fragment
                      key={`${teacherEntryId(teacher) || fullName || 'teacher'}-${index}`}
                    >
                      <span
                        className={`${classNames.teacherName} ${classNames.teacherNameInClassesTable}`}
                      >
                        {fullName}
                      </span>
                      {index < teachers.length - 1 && (
                        <>
                          <span className={classNames.teacherSeparator}>, </span>
                          <wbr />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <span className={classNames.teacherName}>{value}</span>
            )}
          </div>
        );
      },
      xlsxRender: (_value, row) => coTeachersDisplayString(row.teachers, currentTeacherUserId),
    },
    {
      key: 'grade',
      label: t('genericTable.classes.grade'),
      width: '1fr',
      sortable: true,
      render: (_value, row) => (
        <div className={classNames.gradeCell}>
          <span className={classNames.grade}>{commonUtils.getGradeName(row.gradeId, t)}</span>
        </div>
      ),
      xlsxRender: (_value, row) => commonUtils.getGradeName(row.gradeId, t),
    },
    {
      key: 'students',
      label: t('genericTable.classes.students'),
      width: '1fr',
      sortable: true,
      render: (_value, row) => (
        <div className={classNames.studentsCell}>
          <span className={classNames.studentsCount}>{row.students || 0}</span>
        </div>
      ),
      xlsxRender: (_value, row) => `${row.students || 0}`,
    },
    {
      key: 'activeStudents',
      label: t('genericTable.classes.activeStudents'),
      width: '1fr',
      sortable: true,
      render: (_value, row) => (
        <div className={classNames.activeStudentsCell}>
          <span className={classNames.activeStudentsCount}>{row.activeStudents || 0}</span>
          <span className={classNames.activeStudentsPercentage}>
            ({row.percentageOfActiveStudents}%)
          </span>
        </div>
      ),
      xlsxRender: (_value, row) => row.activeStudents || 0,
    },
  ];
}

export function createAdminClassesReportColumns(options: {
  t: (key: string) => string;
  copiedClassId: string | null;
  setCopiedClassId: (id: string | null) => void;
  onCopyClassCode: (message: string) => void;
  onNameClick?: (classId: string) => void;
  dropdownItems: DropdownItem[];
  /** When true, first menu item is never hidden (e.g. teacher “View class” for empty classes). */
  alwaysShowPrimaryRowAction?: boolean;
  currentTeacherUserId?: string;
}): TableColumn<ClassesReportTableRow>[] {
  const { dropdownItems, alwaysShowPrimaryRowAction, ...sharedOptions } = options;
  const base = createSharedClassesReportColumns(sharedOptions);

  return [
    ...base,
    {
      key: 'actions',
      label: '',
      width: '55px',
      render: (_, row) => (
        <div className={classNames.actionsCell}>
          <DropdownMenu
            id={row.id}
            items={dropdownItems}
            hasStudents={alwaysShowPrimaryRowAction ? true : row.students > 0}
          />
        </div>
      ),
    },
  ];
}

export function createTeacherClassesReportColumns(options: {
  t: (key: string) => string;
  copiedClassId: string | null;
  setCopiedClassId: (id: string | null) => void;
  onCopyClassCode: (message: string) => void;
  currentTeacherUserId?: string;
}): TableColumn<ClassesReportTableRow>[] {
  return createSharedClassesReportColumns(options);
}
